import React, { useState, useEffect } from 'react';
import { commandeHistoriqueService, CommandeHistorique } from '../../services/CommandeHistoriqueService';

const HistoriqueCommandes: React.FC = () => {
  const [historique, setHistorique] = useState<CommandeHistorique[]>([]);
  const [recherche, setRecherche] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [statistiques, setStatistiques] = useState({
    totalCommandes: 0,
    commandesEnCours: 0,
    commandesRecues: 0,
    montantTotalCommande: 0,
    derniereMiseAJour: null as string | null,
  });

  useEffect(() => {
    const chargerHistorique = async () => {
      try {
        const data = await commandeHistoriqueService.getHistorique(true);
        setHistorique(data);
        const stats = await commandeHistoriqueService.getStatistiques();
        setStatistiques(stats);
      } catch (error) {
        console.error("Erreur lors du chargement de l'historique:", error);
      } finally {
        setLoading(false);
      }
    };

    chargerHistorique();
  }, []);

  const historiqueFiltre = historique.filter((commande) => {
    if (!recherche) return true;
    const termeLower = recherche.toLowerCase();
    return (
      commande.numeroCommande.toLowerCase().includes(termeLower) ||
      commande.devisInclus.some((ref) => ref.toLowerCase().includes(termeLower)) ||
      (commande.notes && commande.notes.toLowerCase().includes(termeLower))
    );
  });
  // Limiter l'affichage aux 3 dernières commandes (le reste est disponible dans l'historique complet)
  const historiqueAffiche = historiqueFiltre.slice(0, 3);

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatutColor = (statut: string) => {
    switch (statut) {
      case 'BROUILLON':
        return 'bg-blue-100 text-blue-800';
      case 'ENVOYEE':
        return 'bg-yellow-100 text-yellow-800';
      case 'RECUE':
        return 'bg-green-100 text-green-800';
      case 'ANNULEE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatutLabel = (statut: string) => {
    switch (statut) {
      case 'BROUILLON':
        return '📝 Brouillon';
      case 'ENVOYEE':
        return '📤 Envoyée';
      case 'RECUE':
        return '✔ Reçue';
      case 'ANNULEE':
        return '✖ Annulée';
      default:
        return statut;
    }
  };

  const rafraichir = async () => {
    const data = await commandeHistoriqueService.getHistorique(true);
    setHistorique(data);
    const stats = await commandeHistoriqueService.getStatistiques();
    setStatistiques(stats);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900">Historique des commandes fournisseurs</h2>
        <button
          onClick={() => commandeHistoriqueService.exporterHistorique()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Exporter CSV
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">📦</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total commandes</dt>
                  <dd className="text-lg font-medium text-gray-900">{statistiques.totalCommandes}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">⏳</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">En cours</dt>
                  <dd className="text-lg font-medium text-gray-900">{statistiques.commandesEnCours}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">✔</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Reçues</dt>
                  <dd className="text-lg font-medium text-gray-900">{statistiques.commandesRecues}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold">💰</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Montant total</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatMontant(statistiques.montantTotalCommande)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Rechercher par numéro de commande, devis ou notes..."
              className="input-field w-full"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
          </div>
          <button
            onClick={() => setRecherche('')}
            className="bg-gray-100 p-2 rounded-md hover:bg-gray-200"
            title="Effacer la recherche"
          >
            <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Liste de l'historique */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Numéro commande
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Devis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {historiqueAffiche.length > 0 ? (
                historiqueAffiche.map((commande) => (
                  <tr key={commande.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {commande.numeroCommande}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(commande.dateCreation)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatutColor(commande.statut)}`}>
                        {getStatutLabel(commande.statut)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="max-w-xs">
                        <div className="flex flex-wrap gap-1">
                          {commande.devisInclus.slice(0, 3).map((ref, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {ref}
                            </span>
                          ))}
                          {commande.devisInclus.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{commande.devisInclus.length - 3} autres
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatMontant(commande.totaux.montantTotal)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <select
                        value={commande.statut}
                        onChange={async (e) => {
                          const nouveauStatut = e.target.value as CommandeHistorique['statut'];
                          await commandeHistoriqueService.mettreAJourStatutCommande(commande.id, nouveauStatut);
                          await rafraichir();
                        }}
                        className="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="BROUILLON">📝 Brouillon</option>
                        <option value="ENVOYEE">📤 Envoyée</option>
                        <option value="RECUE">✔ Reçue</option>
                        <option value="ANNULEE">✖ Annulée</option>
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    {recherche ? 'Aucune commande ne correspond à votre recherche.' : "Aucune commande dans l'historique."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes d'aide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Informations sur l'historique</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Les commandes sont ajoutées au serveur lors de la génération</li>
                <li>Vous pouvez mettre à jour le statut des commandes pour suivre leur progression</li>
                <li>L'historique est exportable en CSV</li>
                <li>Les 100 dernières commandes sont conservées côté cache local</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoriqueCommandes;
